// Documentação completa dos hooks do sistema Altus Food

export interface HookParam {
  name: string;
  type: string;
  description: string;
  optional?: boolean;
}

export interface HookReturn {
  name: string;
  type: string;
  description: string;
}

export interface HookDocumentation {
  name: string;
  path: string;
  description: string;
  category: string;
  params?: HookParam[];
  returns: HookReturn[];
  usage?: string;
  dependencies?: string[];
}

export const HOOK_CATEGORIES = [
  { id: "auth", name: "Autenticação", icon: "Shield", color: "text-red-500" },
  { id: "orders", name: "Pedidos/PDV", icon: "ShoppingCart", color: "text-blue-500" },
  { id: "products", name: "Produtos", icon: "Package", color: "text-purple-500" },
  { id: "tables", name: "Mesas/Comandas", icon: "LayoutGrid", color: "text-orange-500" },
  { id: "cash", name: "Caixa", icon: "Wallet", color: "text-green-500" },
  { id: "stock", name: "Estoque", icon: "Warehouse", color: "text-yellow-500" },
  { id: "financial", name: "Financeiro", icon: "DollarSign", color: "text-emerald-500" },
  { id: "printing", name: "Impressão", icon: "Printer", color: "text-cyan-500" },
  { id: "offline", name: "Offline/Cache", icon: "WifiOff", color: "text-slate-500" },
  { id: "reports", name: "Relatórios", icon: "BarChart3", color: "text-indigo-500" },
  { id: "fiscal", name: "Fiscal", icon: "Receipt", color: "text-red-500" },
  { id: "notifications", name: "Notificações", icon: "Bell", color: "text-pink-500" },
  { id: "ui", name: "UI/UX", icon: "Palette", color: "text-violet-500" },
];

export const HOOKS_DOCUMENTATION: HookDocumentation[] = [
  // ========== AUTENTICAÇÃO ==========
  {
    name: "useAuth",
    path: "src/hooks/useAuth.ts",
    description: "Gerencia autenticação do usuário. Provê login, logout, registro e recuperação de senha.",
    category: "auth",
    returns: [
      { name: "user", type: "User | null", description: "Usuário autenticado" },
      { name: "session", type: "Session | null", description: "Sessão ativa" },
      { name: "loading", type: "boolean", description: "Carregando estado de auth" },
      { name: "signIn", type: "(email, password) => Promise", description: "Login com email/senha" },
      { name: "signUp", type: "(email, password, metadata) => Promise", description: "Registro de novo usuário" },
      { name: "signOut", type: "() => Promise", description: "Logout" },
      { name: "resetPassword", type: "(email) => Promise", description: "Envia email de reset" },
    ],
    usage: `const { user, signIn, signOut } = useAuth();

// Login
await signIn('email@example.com', 'senha123');

// Verificar se está logado
if (user) {
  console.log('Usuário:', user.email);
}`,
  },
  {
    name: "useUserRole",
    path: "src/hooks/useUserRole.ts",
    description: "Retorna o papel (role) do usuário no restaurante ativo.",
    category: "auth",
    returns: [
      { name: "role", type: "AppRole | null", description: "Papel do usuário (super_admin, restaurant_owner, manager, cashier, waiter, kitchen)" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "isManager", type: "boolean", description: "É gerente ou superior" },
      { name: "isCashier", type: "boolean", description: "É caixa ou superior" },
      { name: "isWaiter", type: "boolean", description: "É garçom" },
    ],
    usage: `const { role, isManager } = useUserRole();

if (isManager) {
  // Mostrar opções de gerente
}`,
  },
  {
    name: "useSuperAdmin",
    path: "src/hooks/useSuperAdmin.ts",
    description: "Verifica se o usuário é super admin da plataforma.",
    category: "auth",
    returns: [
      { name: "isSuperAdmin", type: "boolean", description: "É super admin" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
    ],
  },
  {
    name: "useDailyAccessCode",
    path: "src/hooks/useDailyAccessCode.ts",
    description: "Gerencia códigos de acesso diários para garçons.",
    category: "auth",
    returns: [
      { name: "todayCode", type: "string | null", description: "Código do dia" },
      { name: "generateCode", type: "() => Promise<string>", description: "Gera novo código" },
      { name: "validateCode", type: "(code) => boolean", description: "Valida código" },
    ],
  },

  // ========== PEDIDOS/PDV ==========
  {
    name: "useOrders",
    path: "src/hooks/useOrders.ts",
    description: "Hook principal para gerenciamento de pedidos. Cria, atualiza, fecha pedidos e itens.",
    category: "orders",
    returns: [
      { name: "createOrder", type: "(tableId?, commandId?) => Promise<Order>", description: "Cria novo pedido" },
      { name: "sendItemsToOrder", type: "(orderId, items) => Promise", description: "Adiciona itens ao pedido" },
      { name: "closeOrder", type: "(orderId, payments) => Promise", description: "Fecha/recebe pedido" },
      { name: "cancelItem", type: "(itemId, reason) => Promise", description: "Cancela item" },
      { name: "applyDiscount", type: "(orderId, discount) => Promise", description: "Aplica desconto" },
      { name: "toggleServiceTax", type: "(orderId) => Promise", description: "Liga/desliga taxa de serviço" },
    ],
    usage: `const { createOrder, sendItemsToOrder, closeOrder } = useOrders();

// Criar pedido para mesa
const order = await createOrder(tableId);

// Adicionar itens
await sendItemsToOrder(order.id, [
  { productId: 'xxx', quantity: 2, unitPrice: 25.00 }
]);

// Fechar/receber
await closeOrder(order.id, [
  { paymentMethodId: 'xxx', amount: 50.00 }
]);`,
    dependencies: ["useRestaurantContext", "useSQLiteLocal"],
  },
  {
    name: "useCart",
    path: "src/hooks/useCart.ts",
    description: "Gerencia carrinho local no PDV antes de enviar para cozinha.",
    category: "orders",
    returns: [
      { name: "items", type: "CartItem[]", description: "Itens no carrinho" },
      { name: "addItem", type: "(product, quantity, notes?) => void", description: "Adiciona item" },
      { name: "removeItem", type: "(index) => void", description: "Remove item" },
      { name: "updateQuantity", type: "(index, quantity) => void", description: "Atualiza quantidade" },
      { name: "clear", type: "() => void", description: "Limpa carrinho" },
      { name: "total", type: "number", description: "Total do carrinho" },
    ],
  },
  {
    name: "useCurrentOrderItems",
    path: "src/hooks/useCurrentOrderItems.ts",
    description: "Busca itens do pedido atual de uma mesa/comanda.",
    category: "orders",
    params: [
      { name: "orderId", type: "string", description: "ID do pedido", optional: true },
    ],
    returns: [
      { name: "items", type: "OrderItem[]", description: "Itens do pedido" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "refetch", type: "() => void", description: "Recarrega itens" },
    ],
  },
  {
    name: "useOpenConsumption",
    path: "src/hooks/useOpenConsumption.ts",
    description: "Gerencia consumos abertos (mesas/comandas ocupadas).",
    category: "orders",
    returns: [
      { name: "openConsumptions", type: "OpenConsumption[]", description: "Consumos abertos" },
      { name: "getConsumption", type: "(tableId) => OpenConsumption", description: "Busca consumo" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
    ],
  },
  {
    name: "usePartialPayments",
    path: "src/hooks/usePartialPayments.ts",
    description: "Gerencia pagamentos parciais de um pedido.",
    category: "orders",
    params: [
      { name: "orderId", type: "string", description: "ID do pedido" },
    ],
    returns: [
      { name: "payments", type: "PartialPayment[]", description: "Pagamentos realizados" },
      { name: "totalPaid", type: "number", description: "Total pago" },
      { name: "remaining", type: "number", description: "Valor restante" },
      { name: "addPayment", type: "(methodId, amount) => Promise", description: "Adiciona pagamento" },
    ],
  },
  {
    name: "useOrdersList",
    path: "src/hooks/useOrdersList.ts",
    description: "Lista pedidos com filtros e paginação.",
    category: "orders",
    params: [
      { name: "filters", type: "OrderFilters", description: "Filtros de busca", optional: true },
    ],
    returns: [
      { name: "orders", type: "Order[]", description: "Lista de pedidos" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "totalCount", type: "number", description: "Total de pedidos" },
      { name: "refetch", type: "() => void", description: "Recarrega" },
    ],
  },

  // ========== PRODUTOS ==========
  {
    name: "useProducts",
    path: "src/hooks/useProducts.ts",
    description: "CRUD de produtos do cardápio.",
    category: "products",
    returns: [
      { name: "products", type: "Product[]", description: "Lista de produtos" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "createProduct", type: "(data) => Promise<Product>", description: "Cria produto" },
      { name: "updateProduct", type: "(id, data) => Promise", description: "Atualiza produto" },
      { name: "deleteProduct", type: "(id) => Promise", description: "Exclui produto" },
      { name: "toggleActive", type: "(id) => Promise", description: "Ativa/desativa" },
    ],
    usage: `const { products, createProduct } = useProducts();

// Criar produto
await createProduct({
  name: 'X-Bacon',
  price: 25.00,
  categoryId: 'xxx',
  isActive: true,
});`,
  },
  {
    name: "useCategories",
    path: "src/hooks/useCategories.ts",
    description: "CRUD de categorias de produtos.",
    category: "products",
    returns: [
      { name: "categories", type: "Category[]", description: "Lista de categorias" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "createCategory", type: "(data) => Promise", description: "Cria categoria" },
      { name: "updateCategory", type: "(id, data) => Promise", description: "Atualiza" },
      { name: "deleteCategory", type: "(id) => Promise", description: "Exclui" },
      { name: "reorder", type: "(ids) => Promise", description: "Reordena" },
    ],
  },
  {
    name: "useCombos",
    path: "src/hooks/useCombos.ts",
    description: "CRUD de combos (promoções montáveis).",
    category: "products",
    returns: [
      { name: "combos", type: "Combo[]", description: "Lista de combos" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "createCombo", type: "(data) => Promise<Combo>", description: "Cria combo" },
      { name: "updateCombo", type: "(id, data) => Promise", description: "Atualiza" },
      { name: "deleteCombo", type: "(id) => Promise", description: "Exclui" },
    ],
  },
  {
    name: "useComboSlots",
    path: "src/hooks/useComboSlots.ts",
    description: "Gerencia slots (opções de escolha) dentro de um combo.",
    category: "products",
    params: [
      { name: "comboId", type: "string", description: "ID do combo" },
    ],
    returns: [
      { name: "slots", type: "ComboSlot[]", description: "Slots do combo" },
      { name: "createSlot", type: "(data) => Promise", description: "Cria slot" },
      { name: "updateSlot", type: "(id, data) => Promise", description: "Atualiza" },
      { name: "deleteSlot", type: "(id) => Promise", description: "Exclui" },
    ],
  },
  {
    name: "useProductIngredients",
    path: "src/hooks/useProductIngredients.ts",
    description: "Gerencia ficha técnica (ingredientes de um produto).",
    category: "products",
    params: [
      { name: "productId", type: "string", description: "ID do produto" },
    ],
    returns: [
      { name: "ingredients", type: "ProductIngredient[]", description: "Ingredientes" },
      { name: "addIngredient", type: "(ingredientId, quantity) => Promise", description: "Adiciona" },
      { name: "removeIngredient", type: "(id) => Promise", description: "Remove" },
      { name: "totalCost", type: "number", description: "Custo total da receita" },
    ],
  },
  {
    name: "usePriceHistory",
    path: "src/hooks/usePriceHistory.ts",
    description: "Histórico de alterações de preço de um produto.",
    category: "products",
    params: [
      { name: "productId", type: "string", description: "ID do produto" },
    ],
    returns: [
      { name: "history", type: "PriceChange[]", description: "Histórico de preços" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
    ],
  },

  // ========== MESAS/COMANDAS ==========
  {
    name: "useTables",
    path: "src/hooks/useTables.ts",
    description: "CRUD de mesas com status em tempo real.",
    category: "tables",
    returns: [
      { name: "tables", type: "Table[]", description: "Lista de mesas" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "createTable", type: "(data) => Promise", description: "Cria mesa" },
      { name: "updateTable", type: "(id, data) => Promise", description: "Atualiza" },
      { name: "deleteTable", type: "(id) => Promise", description: "Exclui" },
      { name: "updateStatus", type: "(id, status) => Promise", description: "Muda status" },
    ],
    dependencies: ["useRestaurantContext", "useSQLiteLocal"],
  },
  {
    name: "useCommands",
    path: "src/hooks/useCommands.ts",
    description: "CRUD de comandas.",
    category: "tables",
    returns: [
      { name: "commands", type: "Command[]", description: "Lista de comandas" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "createCommand", type: "(number) => Promise", description: "Cria comanda" },
      { name: "createBatch", type: "(start, end) => Promise", description: "Cria lote" },
    ],
  },
  {
    name: "useCommandEntries",
    path: "src/hooks/useCommandEntries.ts",
    description: "Gerencia entradas de comanda (couvert, taxas).",
    category: "tables",
    returns: [
      { name: "entries", type: "CommandEntry[]", description: "Entradas ativas" },
      { name: "openEntry", type: "(commandId, entryTypeId, amount) => Promise", description: "Abre entrada" },
      { name: "closeEntry", type: "(entryId) => Promise", description: "Fecha entrada" },
    ],
  },
  {
    name: "useEntryTypes",
    path: "src/hooks/useEntryTypes.ts",
    description: "CRUD de tipos de entrada (couvert, show, etc).",
    category: "tables",
    returns: [
      { name: "entryTypes", type: "EntryType[]", description: "Tipos de entrada" },
      { name: "createEntryType", type: "(data) => Promise", description: "Cria tipo" },
      { name: "updateEntryType", type: "(id, data) => Promise", description: "Atualiza" },
    ],
  },

  // ========== CAIXA ==========
  {
    name: "useCashRegister",
    path: "src/hooks/useCashRegister.ts",
    description: "Gerencia caixa (abertura, fechamento, movimentações).",
    category: "cash",
    returns: [
      { name: "openCashRegister", type: "CashRegister | null", description: "Caixa aberto" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "openRegister", type: "(amount) => Promise", description: "Abre caixa" },
      { name: "closeRegister", type: "(closingAmount, notes?) => Promise", description: "Fecha caixa" },
      { name: "addMovement", type: "(type, amount, reason) => Promise", description: "Adiciona movimento" },
    ],
    usage: `const { openCashRegister, openRegister, closeRegister } = useCashRegister();

// Abrir caixa com R$ 200 de troco
await openRegister(200);

// Sangria de R$ 500
await addMovement('withdrawal', 500, 'Sangria para cofre');

// Fechar contando R$ 1500
await closeRegister(1500, 'Fechamento normal');`,
  },
  {
    name: "useCashCount",
    path: "src/hooks/useCashCount.ts",
    description: "Contagem de caixa por denominação (cédulas/moedas).",
    category: "cash",
    returns: [
      { name: "counts", type: "CashCountDetail[]", description: "Contagem por denominação" },
      { name: "total", type: "number", description: "Total contado" },
      { name: "setCount", type: "(denomination, quantity) => void", description: "Define quantidade" },
      { name: "saveCount", type: "(cashRegisterId) => Promise", description: "Salva contagem" },
    ],
  },
  {
    name: "useOperationalPeriod",
    path: "src/hooks/useOperationalPeriod.ts",
    description: "Gerencia período operacional (dia de trabalho).",
    category: "cash",
    returns: [
      { name: "openPeriod", type: "OperationalPeriod | null", description: "Período aberto" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "openNewPeriod", type: "() => Promise", description: "Abre novo período" },
      { name: "closePeriod", type: "() => Promise", description: "Fecha período" },
    ],
  },
  {
    name: "useCashRegisterReport",
    path: "src/hooks/useCashRegisterReport.ts",
    description: "Gera relatório de caixa para impressão.",
    category: "cash",
    params: [
      { name: "cashRegisterId", type: "string", description: "ID do caixa" },
    ],
    returns: [
      { name: "report", type: "CashRegisterReport", description: "Dados do relatório" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "printReport", type: "() => Promise", description: "Imprime relatório" },
    ],
  },

  // ========== ESTOQUE ==========
  {
    name: "useIngredients",
    path: "src/hooks/useIngredients.ts",
    description: "CRUD de ingredientes para controle de estoque.",
    category: "stock",
    returns: [
      { name: "ingredients", type: "Ingredient[]", description: "Lista de ingredientes" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "createIngredient", type: "(data) => Promise", description: "Cria ingrediente" },
      { name: "updateIngredient", type: "(id, data) => Promise", description: "Atualiza" },
      { name: "deleteIngredient", type: "(id) => Promise", description: "Exclui" },
    ],
  },
  {
    name: "useStockMovements",
    path: "src/hooks/useStockMovements.ts",
    description: "Lista movimentações de estoque com filtros.",
    category: "stock",
    params: [
      { name: "params", type: "StockMovementParams", description: "Filtros (ingredientId, tipo, período)", optional: true },
    ],
    returns: [
      { name: "movements", type: "StockMovement[]", description: "Movimentações" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "totalPages", type: "number", description: "Total de páginas" },
      { name: "fetchMovements", type: "() => Promise", description: "Busca movimentações" },
    ],
  },
  {
    name: "useStockAvailability",
    path: "src/hooks/useStockAvailability.ts",
    description: "Verifica disponibilidade de estoque para produtos.",
    category: "stock",
    returns: [
      { name: "checkAvailability", type: "(productId, qty) => boolean", description: "Verifica disponibilidade" },
      { name: "lowStockProducts", type: "Product[]", description: "Produtos com estoque baixo" },
    ],
  },
  {
    name: "useProductStock",
    path: "src/hooks/useProductStock.ts",
    description: "Gerencia estoque de produtos (quando stock_control = true).",
    category: "stock",
    params: [
      { name: "productId", type: "string", description: "ID do produto" },
    ],
    returns: [
      { name: "currentStock", type: "number", description: "Estoque atual" },
      { name: "addStock", type: "(quantity, cost) => Promise", description: "Entrada de estoque" },
      { name: "removeStock", type: "(quantity, reason) => Promise", description: "Saída de estoque" },
      { name: "adjustStock", type: "(newQuantity, reason) => Promise", description: "Ajuste de estoque" },
    ],
  },

  // ========== FINANCEIRO ==========
  {
    name: "useExpenses",
    path: "src/hooks/useExpenses.ts",
    description: "CRUD de despesas (contas a pagar).",
    category: "financial",
    returns: [
      { name: "expenses", type: "Expense[]", description: "Lista de despesas" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
      { name: "createExpense", type: "(data) => Promise", description: "Cria despesa" },
      { name: "updateExpense", type: "(id, data) => Promise", description: "Atualiza" },
      { name: "markAsPaid", type: "(id) => Promise", description: "Marca como paga" },
      { name: "deleteExpense", type: "(id) => Promise", description: "Exclui" },
    ],
  },
  {
    name: "useExpenseCategories",
    path: "src/hooks/useExpenseCategories.ts",
    description: "CRUD de categorias de despesas.",
    category: "financial",
    returns: [
      { name: "categories", type: "ExpenseCategory[]", description: "Categorias" },
      { name: "createCategory", type: "(name) => Promise", description: "Cria categoria" },
    ],
  },
  {
    name: "useChartOfAccounts",
    path: "src/hooks/useChartOfAccounts.ts",
    description: "Gerencia plano de contas para DRE.",
    category: "financial",
    returns: [
      { name: "accounts", type: "ChartAccount[]", description: "Contas" },
      { name: "createAccount", type: "(data) => Promise", description: "Cria conta" },
      { name: "updateAccount", type: "(id, data) => Promise", description: "Atualiza" },
    ],
  },
  {
    name: "usePaymentMethods",
    path: "src/hooks/usePaymentMethods.ts",
    description: "CRUD de formas de pagamento.",
    category: "financial",
    returns: [
      { name: "paymentMethods", type: "PaymentMethod[]", description: "Formas de pagamento" },
      { name: "createMethod", type: "(data) => Promise", description: "Cria forma" },
      { name: "updateMethod", type: "(id, data) => Promise", description: "Atualiza" },
      { name: "toggleActive", type: "(id) => Promise", description: "Ativa/desativa" },
    ],
  },
  {
    name: "useSuppliers",
    path: "src/hooks/useSuppliers.ts",
    description: "CRUD de fornecedores.",
    category: "financial",
    returns: [
      { name: "suppliers", type: "Supplier[]", description: "Fornecedores" },
      { name: "createSupplier", type: "(data) => Promise", description: "Cria fornecedor" },
      { name: "updateSupplier", type: "(id, data) => Promise", description: "Atualiza" },
    ],
  },

  // ========== IMPRESSÃO ==========
  {
    name: "usePrinters",
    path: "src/hooks/usePrinters.ts",
    description: "CRUD de impressoras configuradas.",
    category: "printing",
    returns: [
      { name: "printers", type: "Printer[]", description: "Impressoras" },
      { name: "defaultPrinter", type: "Printer | null", description: "Impressora padrão" },
      { name: "createPrinter", type: "(data) => Promise", description: "Cria impressora" },
      { name: "updatePrinter", type: "(id, data) => Promise", description: "Atualiza" },
      { name: "testPrinter", type: "(id) => Promise", description: "Testa impressão" },
    ],
  },
  {
    name: "usePrintTemplates",
    path: "src/hooks/usePrintTemplates.ts",
    description: "Configuração de templates de impressão.",
    category: "printing",
    returns: [
      { name: "templates", type: "PrintTemplate[]", description: "Templates" },
      { name: "getTemplate", type: "(type) => PrintTemplate", description: "Busca template" },
      { name: "updateTemplate", type: "(type, config) => Promise", description: "Atualiza" },
    ],
  },
  {
    name: "useCentralizedPrintQueue",
    path: "src/hooks/useCentralizedPrintQueue.ts",
    description: "Fila centralizada de impressão com retry automático.",
    category: "printing",
    returns: [
      { name: "queue", type: "PrintJob[]", description: "Fila de impressão" },
      { name: "addToQueue", type: "(job) => void", description: "Adiciona à fila" },
      { name: "processQueue", type: "() => Promise", description: "Processa fila" },
      { name: "retryJob", type: "(jobId) => Promise", description: "Reprocessa job" },
    ],
  },
  {
    name: "useQZTrayConnection",
    path: "src/hooks/useQZTrayConnection.ts",
    description: "Gerencia conexão com QZ Tray para impressão.",
    category: "printing",
    returns: [
      { name: "isConnected", type: "boolean", description: "Conectado ao QZ Tray" },
      { name: "connect", type: "() => Promise", description: "Conecta" },
      { name: "disconnect", type: "() => void", description: "Desconecta" },
      { name: "getPrinters", type: "() => Promise<string[]>", description: "Lista impressoras do SO" },
      { name: "print", type: "(printerName, data) => Promise", description: "Imprime" },
    ],
  },
  {
    name: "usePrintQueueMonitor",
    path: "src/hooks/usePrintQueueMonitor.ts",
    description: "Monitora status da fila de impressão.",
    category: "printing",
    returns: [
      { name: "pendingJobs", type: "number", description: "Jobs pendentes" },
      { name: "failedJobs", type: "number", description: "Jobs com falha" },
      { name: "recentJobs", type: "PrintJob[]", description: "Jobs recentes" },
    ],
  },

  // ========== OFFLINE/CACHE ==========
  {
    name: "useSQLiteLocal",
    path: "src/hooks/useSQLiteLocal.ts",
    description: "Gerencia SQLite WebAssembly para cache local offline.",
    category: "offline",
    returns: [
      { name: "db", type: "Database | null", description: "Instância do banco" },
      { name: "isReady", type: "boolean", description: "Banco pronto" },
      { name: "execute", type: "(sql, params) => any[]", description: "Executa SQL" },
      { name: "getActiveConsumption", type: "(entityId) => Consumption", description: "Busca consumo" },
      { name: "upsertActiveConsumption", type: "(data) => void", description: "Salva consumo" },
    ],
  },
  {
    name: "useSQLiteSync",
    path: "src/hooks/useSQLiteSync.ts",
    description: "Sincroniza dados entre SQLite local e Supabase.",
    category: "offline",
    returns: [
      { name: "syncStatus", type: "SyncStatus", description: "Status da sincronização" },
      { name: "lastSync", type: "Date | null", description: "Última sincronização" },
      { name: "syncNow", type: "() => Promise", description: "Força sincronização" },
    ],
  },
  {
    name: "useOfflineSync",
    path: "src/hooks/useOfflineSync.ts",
    description: "Hook base para operações offline com IndexedDB.",
    category: "offline",
    returns: [
      { name: "isOnline", type: "boolean", description: "Tem conexão" },
      { name: "pendingOperations", type: "number", description: "Operações pendentes" },
      { name: "syncPending", type: "() => Promise", description: "Sincroniza pendentes" },
    ],
  },
  {
    name: "useOfflineSyncExtended",
    path: "src/hooks/useOfflineSyncExtended.ts",
    description: "Sincronização avançada: pedidos, itens e fechamentos offline.",
    category: "offline",
    returns: [
      { name: "syncAll", type: "() => Promise", description: "Sincroniza tudo" },
      { name: "pendingOrders", type: "number", description: "Pedidos pendentes" },
      { name: "pendingItems", type: "number", description: "Itens pendentes" },
      { name: "pendingClosures", type: "number", description: "Fechamentos pendentes" },
      { name: "isSyncing", type: "boolean", description: "Sincronizando" },
    ],
    dependencies: ["useRestaurantContext", "useSQLiteLocal"],
  },

  // ========== RELATÓRIOS ==========
  {
    name: "useReports",
    path: "src/hooks/useReports.ts",
    description: "Hook base para relatórios de vendas.",
    category: "reports",
    params: [
      { name: "startDate", type: "Date", description: "Data inicial" },
      { name: "endDate", type: "Date", description: "Data final" },
    ],
    returns: [
      { name: "salesData", type: "SalesData[]", description: "Dados de vendas" },
      { name: "totalSales", type: "number", description: "Total de vendas" },
      { name: "totalOrders", type: "number", description: "Total de pedidos" },
      { name: "isLoading", type: "boolean", description: "Carregando" },
    ],
  },
  {
    name: "useDREReport",
    path: "src/hooks/useDREReport.ts",
    description: "Gera DRE (Demonstrativo de Resultado).",
    category: "reports",
    params: [
      { name: "month", type: "number", description: "Mês (1-12)" },
      { name: "year", type: "number", description: "Ano" },
    ],
    returns: [
      { name: "dreData", type: "DREData", description: "Dados do DRE" },
      { name: "grossRevenue", type: "number", description: "Receita bruta" },
      { name: "netProfit", type: "number", description: "Lucro líquido" },
      { name: "profitMargin", type: "number", description: "Margem de lucro %" },
    ],
  },
  {
    name: "useCMVReport",
    path: "src/hooks/useCMVReport.ts",
    description: "Relatório de CMV (Custo de Mercadoria Vendida).",
    category: "reports",
    returns: [
      { name: "cmvData", type: "CMVData[]", description: "Dados por produto" },
      { name: "totalCMV", type: "number", description: "CMV total" },
      { name: "cmvPercentage", type: "number", description: "CMV % sobre vendas" },
    ],
  },
  {
    name: "useTicketMedioReport",
    path: "src/hooks/useTicketMedioReport.ts",
    description: "Relatório de ticket médio por período.",
    category: "reports",
    returns: [
      { name: "ticketData", type: "TicketData[]", description: "Dados de ticket" },
      { name: "averageTicket", type: "number", description: "Ticket médio geral" },
    ],
  },
  {
    name: "useCancellationsReport",
    path: "src/hooks/useCancellationsReport.ts",
    description: "Relatório de cancelamentos.",
    category: "reports",
    returns: [
      { name: "cancellations", type: "Cancellation[]", description: "Cancelamentos" },
      { name: "totalCancelled", type: "number", description: "Total cancelado R$" },
      { name: "cancellationRate", type: "number", description: "Taxa de cancelamento %" },
    ],
  },
  {
    name: "useRatingsReport",
    path: "src/hooks/useRatingsReport.ts",
    description: "Relatório de avaliações de clientes.",
    category: "reports",
    returns: [
      { name: "ratings", type: "Rating[]", description: "Avaliações" },
      { name: "averageRating", type: "number", description: "Média geral" },
      { name: "nps", type: "number", description: "NPS score" },
    ],
  },
  {
    name: "useDashboardMetrics",
    path: "src/hooks/useDashboardMetrics.ts",
    description: "Métricas do dashboard (vendas hoje, pedidos, etc).",
    category: "reports",
    returns: [
      { name: "todaySales", type: "number", description: "Vendas hoje" },
      { name: "todayOrders", type: "number", description: "Pedidos hoje" },
      { name: "openTables", type: "number", description: "Mesas ocupadas" },
      { name: "pendingOrders", type: "number", description: "Pedidos em preparo" },
    ],
  },

  // ========== FISCAL ==========
  {
    name: "useFiscalConfig",
    path: "src/hooks/useFiscalConfig.ts",
    description: "Configurações fiscais (certificado, CSC, ACBr).",
    category: "fiscal",
    returns: [
      { name: "config", type: "FiscalConfig | null", description: "Configuração atual" },
      { name: "loading", type: "boolean", description: "Carregando" },
      { name: "createOrUpdateConfig", type: "(data) => Promise", description: "Salva configuração" },
    ],
  },
  {
    name: "useNfceEmission",
    path: "src/hooks/useNfceEmission.ts",
    description: "Emissão de NFC-e via ACBrMonitor.",
    category: "fiscal",
    returns: [
      { name: "emit", type: "(orderId) => Promise<NfceResult>", description: "Emite NFC-e" },
      { name: "isEmitting", type: "boolean", description: "Emitindo" },
      { name: "lastResult", type: "NfceResult | null", description: "Último resultado" },
    ],
  },
  {
    name: "useNfceHistory",
    path: "src/hooks/useNfceHistory.ts",
    description: "Histórico de NFC-e emitidas.",
    category: "fiscal",
    returns: [
      { name: "nfces", type: "NfceIssued[]", description: "Notas emitidas" },
      { name: "loading", type: "boolean", description: "Carregando" },
      { name: "fetchNfces", type: "(filters?) => Promise", description: "Busca com filtros" },
    ],
  },
  {
    name: "useProductsFiscal",
    path: "src/hooks/useProductsFiscal.ts",
    description: "Dados fiscais dos produtos (NCM, CFOP, alíquotas).",
    category: "fiscal",
    returns: [
      { name: "fiscalData", type: "ProductFiscal[]", description: "Dados fiscais" },
      { name: "updateFiscal", type: "(productId, data) => Promise", description: "Atualiza dados" },
      { name: "batchUpdate", type: "(productIds, data) => Promise", description: "Atualiza em lote" },
    ],
  },
  {
    name: "useTaxRules",
    path: "src/hooks/useTaxRules.ts",
    description: "Regras fiscais por UF/NCM.",
    category: "fiscal",
    returns: [
      { name: "taxRules", type: "TaxRule[]", description: "Regras" },
      { name: "getRuleFor", type: "(ncm, uf) => TaxRule", description: "Busca regra" },
    ],
  },

  // ========== NOTIFICAÇÕES ==========
  {
    name: "useNotifications",
    path: "src/hooks/useNotifications.ts",
    description: "Notificações internas do sistema.",
    category: "notifications",
    returns: [
      { name: "notifications", type: "Notification[]", description: "Notificações" },
      { name: "unreadCount", type: "number", description: "Não lidas" },
      { name: "markAsRead", type: "(id) => Promise", description: "Marca como lida" },
      { name: "markAllAsRead", type: "() => Promise", description: "Marca todas" },
    ],
  },
  {
    name: "usePushNotifications",
    path: "src/hooks/usePushNotifications.ts",
    description: "Notificações push via Service Worker.",
    category: "notifications",
    returns: [
      { name: "isSupported", type: "boolean", description: "Navegador suporta" },
      { name: "isSubscribed", type: "boolean", description: "Inscrito" },
      { name: "subscribe", type: "() => Promise", description: "Inscreve" },
      { name: "unsubscribe", type: "() => Promise", description: "Cancela" },
    ],
  },
  {
    name: "usePublicOrderNotifications",
    path: "src/hooks/usePublicOrderNotifications.ts",
    description: "Notificações de pedidos do menu público.",
    category: "notifications",
    returns: [
      { name: "newOrders", type: "Order[]", description: "Novos pedidos" },
      { name: "acknowledge", type: "(orderId) => void", description: "Confirma recebimento" },
    ],
  },
  {
    name: "useWaiterCallNotifications",
    path: "src/hooks/useWaiterCallNotifications.ts",
    description: "Chamadas de garçom das mesas.",
    category: "notifications",
    returns: [
      { name: "calls", type: "WaiterCall[]", description: "Chamadas ativas" },
      { name: "respondToCall", type: "(callId) => Promise", description: "Responde chamada" },
    ],
  },

  // ========== UI/UX ==========
  {
    name: "useMobile",
    path: "src/hooks/use-mobile.tsx",
    description: "Detecta se é dispositivo mobile.",
    category: "ui",
    returns: [
      { name: "isMobile", type: "boolean", description: "É mobile" },
    ],
  },
  {
    name: "usePWA",
    path: "src/hooks/usePWA.ts",
    description: "Funcionalidades de PWA (instalação, atualização).",
    category: "ui",
    returns: [
      { name: "isInstalled", type: "boolean", description: "App instalado" },
      { name: "canInstall", type: "boolean", description: "Pode instalar" },
      { name: "install", type: "() => Promise", description: "Instala PWA" },
      { name: "updateAvailable", type: "boolean", description: "Atualização disponível" },
      { name: "update", type: "() => void", description: "Aplica atualização" },
    ],
  },
  {
    name: "useVersionCheck",
    path: "src/hooks/useVersionCheck.ts",
    description: "Verifica versão do app e atualizações.",
    category: "ui",
    returns: [
      { name: "currentVersion", type: "string", description: "Versão atual" },
      { name: "latestVersion", type: "string", description: "Versão mais recente" },
      { name: "needsUpdate", type: "boolean", description: "Precisa atualizar" },
    ],
  },
  {
    name: "usePullToRefresh",
    path: "src/hooks/usePullToRefresh.ts",
    description: "Pull-to-refresh para mobile.",
    category: "ui",
    params: [
      { name: "onRefresh", type: "() => Promise", description: "Callback de refresh" },
    ],
    returns: [
      { name: "isRefreshing", type: "boolean", description: "Atualizando" },
      { name: "pullProgress", type: "number", description: "Progresso do pull (0-1)" },
    ],
  },
  {
    name: "useBarcodeScanner",
    path: "src/hooks/useBarcodeScanner.ts",
    description: "Leitura de código de barras via teclado.",
    category: "ui",
    params: [
      { name: "onScan", type: "(barcode) => void", description: "Callback ao escanear" },
    ],
    returns: [
      { name: "isListening", type: "boolean", description: "Escutando" },
      { name: "lastScanned", type: "string | null", description: "Último código" },
    ],
  },
  {
    name: "useTabletIdleTimer",
    path: "src/hooks/useTabletIdleTimer.ts",
    description: "Timer de inatividade para tablets (screensaver).",
    category: "ui",
    params: [
      { name: "timeout", type: "number", description: "Tempo em ms" },
    ],
    returns: [
      { name: "isIdle", type: "boolean", description: "Inativo" },
      { name: "resetTimer", type: "() => void", description: "Reseta timer" },
    ],
  },
];

// Helper para buscar hooks por categoria
export function getHooksByCategory(categoryId: string): HookDocumentation[] {
  return HOOKS_DOCUMENTATION.filter(h => h.category === categoryId);
}

// Helper para buscar um hook específico
export function getHookByName(name: string): HookDocumentation | undefined {
  return HOOKS_DOCUMENTATION.find(h => h.name === name);
}
